import assert from 'assert'
import { z } from 'zod'
import { bufferToHex } from '@ethereumjs/util'
import { ValueObject } from '@lukso/controllerapps-sharedlogic-ddd-core'
import { HexNumber } from '@lukso/controllerapps-sharedlogic-primitives'

const PrivateKeyPropsSchema = z.object({
  privateKeyValue: z.instanceof(Buffer),
})

type PrivateKeyProps = z.infer<typeof PrivateKeyPropsSchema>

export class PrivateKey extends ValueObject<PrivateKeyProps> {
  public getValue(): Buffer {
    return this.props.privateKeyValue
  }

  /**
   * @returns A 0x-prefixed hex string of the private key. The hex string is lowercase.
   */
  public getHexValue(): string {
    return bufferToHex(this.props.privateKeyValue).toLowerCase()
  }

  public equals(other: PrivateKey): boolean {
    return this.getHexValue() === other.getHexValue()
  }

  public static create(props: PrivateKeyProps): PrivateKey {
    const validatedProps = PrivateKeyPropsSchema.parse(props)
    const { privateKeyValue } = validatedProps
    assert(
      privateKeyValue.byteLength === 32,
      `privateKeyValue must be 32 bytes. Got ${privateKeyValue.byteLength}`
    )
    return new PrivateKey(props)
  }

  public static createFromHexNumber(hexNumber: HexNumber): PrivateKey {
    const hexNumberValueWith0x = hexNumber.getValue()
    const hexNumberValue = hexNumberValueWith0x.slice(2)
    const privateKeyValue = Buffer.from(hexNumberValue, 'hex')
    return PrivateKey.create({ privateKeyValue })
  }
}
